import { isObject } from './utils.js';
import { mutationTypes, categories, createEvent } from './types.js';
export default class People {
    constructor(constantProperties, getTimestamp, onId, batcher) {
        this.constantProperties = constantProperties;
        this.getTimestamp = getTimestamp;
        this.onId = onId;
        this.batcher = batcher;
        this.ownProperties = {};
        this.identify = (user_id, options) => {
            if (!user_id || typeof user_id !== 'string') {
                throw new Error('OR SDK: user_id (string) is required for .identify()');
            }
            // if user exists already, reset properties
            if (this.constantProperties.user_id && this.constantProperties.user_id !== user_id) {
                this.reset();
            }
            this.constantProperties.setUserId(user_id);
            if (!options?.fromTracker) {
                this.onId(user_id);
            }
            const identityEvent = createEvent(categories.people, mutationTypes.identity, this.getTimestamp(), { user_id });
            this.batcher.addEvent(identityEvent);
        };
        /** Resets user id and own properties
         *
         * !hard reset will destroy persistent device id!
         * */
        this.reset = (hard) => {
            this.constantProperties.resetUserId(hard);
            this.ownProperties = {};
        };
        /**
         *  Will delete user and its data from backend, then reset all local properties
         */
        this.deleteUser = () => {
            const removedUser = this.constantProperties.user_id;
            if (!removedUser)
                return;
            this.constantProperties.setUserId(null);
            this.ownProperties = {};
            const deleteEvent = createEvent(categories.people, mutationTypes.deleteUser, undefined, {
                user_id: removedUser,
            });
            this.batcher.addEvent(deleteEvent);
            this.reset();
        };
        /**
         * set user properties, overwriting existing ones
         * */
        this.setProperties = (propertyOrObj, value) => {
            if (!propertyOrObj) {
                throw new Error('OR SDK: no user properties provided to set');
            }
            const properties = {};
            if (typeof propertyOrObj === 'string' && propertyOrObj && value) {
                properties[propertyOrObj] = value;
            }
            else if (isObject(propertyOrObj)) {
                Object.assign(properties, propertyOrObj);
            }
            else {
                throw new Error('OR SDK: invalid user properties provided to set');
            }
            Object.entries(properties).forEach(([key, value]) => {
                if (!this.constantProperties.defaultPropertyKeys.includes(key)) {
                    this.ownProperties[key] = value;
                }
            });
            const setEvent = createEvent(categories.people, mutationTypes.setProperty, undefined, {
                user_id: this.user_id,
                properties,
            });
            this.batcher.addEvent(setEvent);
        };
        /**
         * Set property if it doesn't exist yet
         * */
        this.setPropertiesOnce = (properties) => {
            if (!isObject(properties)) {
                throw new Error('Properties must be an object');
            }
            Object.entries(properties).forEach(([key, value]) => {
                if (!this.constantProperties.defaultPropertyKeys.includes(key) && !this.ownProperties[key]) {
                    this.ownProperties[key] = value;
                }
            });
            const setEvent = createEvent(categories.people, mutationTypes.setPropertyOnce, undefined, {
                user_id: this.user_id,
                properties,
            });
            this.batcher.addEvent(setEvent);
        };
        /**
         * Add value to property (will turn string prop into array)
         * */
        this.appendValues = (key, value) => {
            if (!this.constantProperties.defaultPropertyKeys.includes(key) && this.ownProperties[key]) {
                if (Array.isArray(this.ownProperties[key])) {
                    this.ownProperties[key].push(value);
                }
                else {
                    this.ownProperties[key] = [this.ownProperties[key], value];
                }
            }
            const appendEvent = createEvent(categories.people, mutationTypes.appendProperty, undefined, {
                properties: { [key]: value },
                user_id: this.user_id,
            });
            this.batcher.addEvent(appendEvent);
        };
        /**
         * Add unique values to property (will turn string prop into array)
         * */
        this.appendUniqueValues = (key, value) => {
            if (!this.ownProperties[key])
                return;
            if (Array.isArray(this.ownProperties[key])) {
                if (!this.ownProperties[key].includes(value)) {
                    this.appendValues(key, value);
                }
            }
            else if (this.ownProperties[key] !== value) {
                this.appendValues(key, value);
            }
            const unionEvent = createEvent(categories.people, mutationTypes.appendUniqueProperty, undefined, {
                properties: { [key]: value },
                user_id: this.user_id,
            });
            this.batcher.addEvent(unionEvent);
        };
        /**
         * Adds value (incl. negative) to existing numerical property
         * */
        this.increment = (key, value) => {
            if (!this.ownProperties[key]) {
                this.ownProperties[key] = 0;
            }
            if (this.ownProperties[key] && typeof this.ownProperties[key] !== 'number') {
                throw new Error('OR SDK: Property must be a number to increment');
            }
            // @ts-ignore
            this.ownProperties[key] += value;
            const incrementEvent = createEvent(categories.people, mutationTypes.incrementProperty, undefined, {
                user_id: this.user_id,
                properties: { [key]: value },
            });
            this.batcher.addEvent(incrementEvent);
        };
        /** mixpanel compatibility */
        this.union = this.appendUniqueValues;
        this.set = this.setProperties;
        this.set_once = this.setPropertiesOnce;
        this.append = this.appendValues;
        this.incrementBy = this.increment;
    }
    get user_id() {
        return this.constantProperties.user_id;
    }
}
